import {
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	Renderer2,
	ViewChild,
} from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppColumnItemEntity } from '@datachain/ui-sdk/components/app-columns-configurator';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import {
	AppMultiSelectMenuComponent,
	MultiSelectMenuOptionEntity,
} from '@datachain/ui-sdk/components/app-multi-select-menu';
import { ButtonToggle } from '@datachain/ui-sdk/components/app-toggle-buttons';
import { IBreadCrumbEntry } from '@datachain/ui-sdk/components/breadcrumb';
import {
	FieldConfig,
	QueryBuilderGroupConfig,
} from '@datachain/ui-sdk/components/query-builder';
import { randNumber } from '@ngneat/falso';
import { Store } from '@ngrx/store';
import { Map, Set } from 'immutable';
import {
	BehaviorSubject,
	filter,
	firstValueFrom,
	map,
	Observable,
	ReplaySubject,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';

import { configuration } from '../../../core/configuration';
import { ColumnTypeToDxColumnType } from '../../../core/dc-core-types';
import { goToHdHome, goToProjects } from '../../../core/store';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ColumnModelItemEntity } from '../../domain/column-model-item.entity';
import { FilterModelItemEntity } from '../../domain/filter-model-item.entity';
import { ProfileItemEntity } from '../../domain/profile-item.entity';
import { PublicMetadataEntity } from '../../domain/public-metadata.entity';
import {
	SimulationDataSource,
	SimulationSortColumnType,
	SubscriptionStatus,
} from '../../domain/subscription.types';
import {
	getCurrentSubscriptionSimulationConfig,
	getProfilesModelsForSimulation,
	getSubscriptionSimulationData,
	navigateToSubscriptionDetails,
	navigateToSubscriptionList,
	resetSimulationData,
	resetSubscriptionSimulation,
	SubscriptionSimulatorSelector,
} from '../../store';
import { ColumnsQueryFieldsProvider } from '../filter-models/filter-model-config-modal/columns-query-fields-provider.service';
import { ModelsSelectionModalComponent } from './models-selection-modal/models-selection-modal.component';
import { ProfileSelectionModalComponent } from './profile-selection-modal/profile-selection-modal.component';
import { SimulationColumnListProvider } from './simulation-column-list.provider';
import { SimulationColumnsItemEntity } from './simulation-columns-item.entity';
import { SimulationColumnsListEntity } from './simulation-columns-list.entity';
import { SimulationResultEntity } from './simulation-result.entity';
import { SimulationViewProvider } from './simulation-view.provider';

type SimulationType = 'PROFILE' | 'MODELS' | 'NONE';

type SelectElement = {
	id: string;
	label: string;
	type: 'PROFILE' | 'COLUMN_MODEL' | 'FILTER_MODEL';
};

type SubscriptionOverlayFilterModel = {
	filterModelId: string | undefined;
	query: QueryBuilderGroupConfig;
};

@Component({
	selector: 'app-subscription-simulator',
	templateUrl: './subscription-simulator.component.html',
	styleUrls: ['./subscription-simulator.component.scss'],
	inputs: ['subscriptionId', 'projectLabel'],
	providers: [
		SimulationColumnListProvider,
		SimulationViewProvider,
		ColumnsQueryFieldsProvider,
	],
})
export class SubscriptionSimulatorComponent
	extends DcBaseComponent
	implements OnInit, OnDestroy
{
	protected readonly DcIcons = DcIcons;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly Configuration = configuration;
	protected readonly SubscriptionStatus = SubscriptionStatus;

	public paths: Array<IBreadCrumbEntry> = [];

	@ViewChild('ColumnsMultiSelectMenu')
	private readonly columnsMultiSelectMenuCmp: AppMultiSelectMenuComponent | null =
		null;

	@ViewChild('SortColumnsMultiSelectMenu')
	private readonly sortColumnsMultiSelectMenuCmp: AppMultiSelectMenuComponent | null =
		null;

	public simulateTypeOptions: Array<ButtonToggle> = [];

	public dataSourceType: Array<ButtonToggle> = [];

	public vo$: Observable<{
		simulationMetadata: PublicMetadataEntity;
		simulationType: SimulationType;
		selectedSimElements: Set<SelectElement>;
		openedOverlayFilterModel: SubscriptionOverlayFilterModel;
		availableFields: Array<FieldConfig>;
		simulationColumnList: SimulationColumnsListEntity;
		columnOptions: Map<number, MultiSelectMenuOptionEntity>;
		selectedColumns: Map<number, AppColumnItemEntity>;
		sortColumnOptions: Map<number, MultiSelectMenuOptionEntity>;
		sortSelectedColumns: Map<number, AppColumnItemEntity>;
		isSimulationLoading: boolean;
		simulationResult: SimulationResultEntity | null;
		simulationResultColumns: Map<string, SimulationColumnsItemEntity>;
	}>;

	private currentSubscription$ = new Observable<number>();

	private readonly availableColumns$ = new ReplaySubject<
		Map<number, SimulationColumnsItemEntity>
	>(1);

	private readonly simulationResultColumns$ = new BehaviorSubject<
		Map<string, SimulationColumnsItemEntity>
	>(Map());

	private readonly columnOptions$ = new BehaviorSubject<
		Map<number, MultiSelectMenuOptionEntity>
	>(Map());

	private readonly selectedColOpts$ = new BehaviorSubject<
		Map<number, AppColumnItemEntity>
	>(Map());

	private readonly sortColumnOptions$ = new BehaviorSubject<
		Map<number, MultiSelectMenuOptionEntity>
	>(Map());

	private readonly sortSelectedColOpts$ = new BehaviorSubject<
		Map<number, AppColumnItemEntity>
	>(Map());

	private readonly simulationType$ = new BehaviorSubject<SimulationType>(
		'PROFILE'
	);

	private readonly selectedSimElements$ = new BehaviorSubject<
		Set<SelectElement>
	>(Set());

	private readonly openedOverlayFilterModel$ =
		new BehaviorSubject<SubscriptionOverlayFilterModel>({
			filterModelId: undefined,
			query: {
				condition: 'or',
				rules: [],
			},
		});

	private readonly selectedDatasource$ =
		new ReplaySubject<SimulationDataSource>(1);

	private readonly simulationColumns$ = new BehaviorSubject<
		Map<string, SimulationColumnsItemEntity>
	>(Map());

	private readonly availableFields$ = new ReplaySubject<Array<FieldConfig>>(1);

	public constructor(
		private readonly store: Store,
		private readonly simulatorSelector: SubscriptionSimulatorSelector,
		private readonly simulationColumnListProvider: SimulationColumnListProvider,
		private readonly simulationViewProvider: SimulationViewProvider,
		private readonly columnsQueryBuilderProvider: ColumnsQueryFieldsProvider,
		private readonly modalService: AppModalService,
		private readonly renderer: Renderer2,
		private readonly elemRef: ElementRef
	) {
		super();
		this.cmpId = 'subscription-simulator';

		const simulationColumnList$ = this.simulationColumns$.pipe(
			tap((items) => {
				const availableColumns = Map<number, SimulationColumnsItemEntity>(
					items
						.toList()
						.toArray()
						.map((col) => [randNumber(), col])
				);
				this.availableColumns$.next(availableColumns);
			}),
			map((items) => this.simulationColumnListProvider.setListElements(items))
		);

		this.vo$ = combineAll({
			simulationMetadata: this.simulatorSelector.getSimulationMetadata$(),
			simulationType: this.simulationType$,
			selectedSimElements: this.selectedSimElements$,
			openedOverlayFilterModel: this.openedOverlayFilterModel$,
			availableFields: this.availableFields$,
			simulationColumnList: simulationColumnList$,
			columnOptions: this.columnOptions$,
			selectedColumns: this.selectedColOpts$,
			sortColumnOptions: this.sortColumnOptions$,
			sortSelectedColumns: this.sortSelectedColOpts$,
			isSimulationLoading: this.simulatorSelector.isSimulationLoading$(),
			simulationResult: this.simulatorSelector.getSimulationResult$(),
			simulationResultColumns: this.simulationResultColumns$,
		});
	}

	public ngOnInit(): void {
		this.currentSubscription$ = this.toObservable<number>('subscriptionId');
		super.ngOnInit();
		this.triggerInit();
		this.handleSubscription();
	}

	public ngOnDestroy(): void {
		super.ngOnDestroy();

		this.store.dispatch(resetSubscriptionSimulation());
	}

	public showColumnsMultiSelectMenu(event: Event): void {
		event.stopPropagation();
		this.columnsMultiSelectMenuCmp?.openDropdown();
	}

	public showColumnsToSortMultiSelectMenu(event: Event): void {
		event.stopPropagation();
		this.sortColumnsMultiSelectMenuCmp?.openDropdown();
	}

	public async openOverlay(event: Event, item: SelectElement): Promise<void> {
		event.stopPropagation();

		if (item.type !== 'FILTER_MODEL') {
			return;
		}

		const previous = await firstValueFrom(this.openedOverlayFilterModel$);

		if (previous.filterModelId === item.id) {
			return this.openedOverlayFilterModel$.next({
				...previous,
				filterModelId: undefined,
				query: {
					condition: 'or',
					rules: [],
				},
			});
		}

		const filterModelsForSimulation = await firstValueFrom(
			this.simulatorSelector.getFilterModelsForSimulation$()
		);

		const selectedFilterModel = filterModelsForSimulation.get(item.id);

		if (!selectedFilterModel) {
			return;
		}

		this.openedOverlayFilterModel$.next({
			...previous,
			filterModelId: item.id,
			query: selectedFilterModel.root,
		});
	}

	public closeOverlays(): void {
		this.openedOverlayFilterModel$.next({
			filterModelId: undefined,
			query: {
				condition: 'or',
				rules: [],
			},
		});
	}

	public getAvailableSimulationData(simType: SimulationType): void {
		const currentSimulationType = this.simulationType$.value;

		if (currentSimulationType === simType) {
			return;
		}

		this.simulationType$.next(simType);

		if (this.selectedSimElements$.value.size !== 0) {
			this.selectedSimElements$.next(Set());
			this.simulationColumns$.next(Map());
			this.resetSimulation();
		}
	}

	public async select(): Promise<void> {
		const subscriptionProfiles = await firstValueFrom(
			this.simulatorSelector.getProfilesForSimulation$()
		);

		const subscriptionColumnModels = await firstValueFrom(
			this.simulatorSelector.getColumnModelsForSimulation$()
		);
		if (
			subscriptionProfiles.size === 0 &&
			subscriptionColumnModels.size === 0
		) {
			const subscriptionId = await firstValueFrom(this.currentSubscription$);

			this.store.dispatch(
				getProfilesModelsForSimulation({
					subscriptionId,
				})
			);
		}

		const simulationType = await firstValueFrom(this.simulationType$);
		if (simulationType === 'PROFILE') {
			return await this.selectSimulationProfile();
		}

		await this.selectSimulationModels();
	}

	public async selectSimulationProfile(): Promise<void> {
		const selectedSimElements = await firstValueFrom(this.selectedSimElements$);

		const modalRef = this.modalService.openModal<{
			selectedProfileId: string;
		}>(
			ProfileSelectionModalComponent,
			{
				title: $localize`:i18n=@@subscription.simulator.selectProfile.title:`,
				subTitle: '',
				selectedProfileId: selectedSimElements.first()?.id ?? '',
			},
			{
				disableClose: true,
			}
		);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		const [firstSelectedProfile]: Array<ProfileItemEntity> =
			modalResult.profiles;

		const columnModels = await firstValueFrom(
			this.simulatorSelector.getColumnModelsForSimulation$()
		);

		const columnModelForSelectedProfile = columnModels.get(
			firstSelectedProfile.columnModelId
		);

		if (!columnModelForSelectedProfile) {
			return;
		}

		this.resetSimulation();
		this.simulationColumns$.next(
			this.generateSimulationColumns(columnModelForSelectedProfile)
		);
		this.selectedSimElements$.next(
			Set([
				{
					id: firstSelectedProfile.id,
					label: firstSelectedProfile.label,
					type: 'PROFILE',
				} as SelectElement,
			])
		);
	}

	public async selectSimulationModels(): Promise<void> {
		const selectedSimElements = await firstValueFrom(this.selectedSimElements$);

		const selectedColumnModel = selectedSimElements.find(
			(s) => s.type === 'COLUMN_MODEL'
		);

		const selectedFilterModels = selectedColumnModel
			? selectedSimElements.subtract([selectedColumnModel])
			: selectedSimElements;

		const modalRef = this.modalService.openModal<{
			selectedColumnModelId: string;
			selectedFilterModelIds: Set<string>;
		}>(
			ModelsSelectionModalComponent,
			{
				title: $localize`:i18n=@@subscription.simulator.selectModels.title:`,
				subTitle: '',
				selectedColumnModelId: selectedColumnModel
					? selectedColumnModel.id
					: '',
				selectedFilterModelIds: selectedFilterModels.map((f) => f.id),
			},
			{
				disableClose: true,
			}
		);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		const {
			columnModels,
			filterModels,
		}: {
			columnModels: Array<ColumnModelItemEntity>;
			filterModels: Array<FilterModelItemEntity>;
		} = modalResult;

		const [firstSelectedColumnModel] = columnModels;

		const columnModelSelection = Set<SelectElement>([
			{
				id: firstSelectedColumnModel.id,
				label: firstSelectedColumnModel.label,
				type: 'COLUMN_MODEL',
			},
		]);

		const selectedElements = filterModels.reduce(
			(acc: Set<SelectElement>, filterModel) =>
				acc.add({
					id: filterModel.id,
					label: filterModel.label,
					type: 'FILTER_MODEL',
				}),
			columnModelSelection
		);

		this.resetSimulation();
		this.selectedSimElements$.next(selectedElements);
		this.simulationColumns$.next(
			this.generateSimulationColumns(firstSelectedColumnModel)
		);
	}

	public async selectedColumns(ids: Array<string | number>): Promise<void> {
		const columnOptions = await firstValueFrom(this.columnOptions$);
		const selectedColumns = await firstValueFrom(this.selectedColOpts$);

		const selectedOptions = columnOptions
			.filter((_, key) => ids.includes(key))
			.map<AppColumnItemEntity>((option, key) =>
				AppColumnItemEntity.build({
					id: key,
					label: option.label,
					type: option.type,
					isList: option.isList,
				})
			);

		const mergedSelected = selectedColumns.merge(selectedOptions);

		this.selectedColOpts$.next(mergedSelected);

		const availableColumns = await firstValueFrom(this.availableColumns$);

		const updatedOptions = availableColumns
			.filter((_, key) => !mergedSelected.keySeq().toArray().includes(key))
			.map<MultiSelectMenuOptionEntity>((item, key) =>
				MultiSelectMenuOptionEntity.build({
					id: key,
					label: item.label,
					type: item.type,
					isList: item.isList,
				})
			);

		this.columnOptions$.next(updatedOptions);
	}

	public async sortSelectedColumns(ids: Array<string | number>): Promise<void> {
		const sortColumnOptions = await firstValueFrom(this.sortColumnOptions$);
		const sortSelectedColumns = await firstValueFrom(this.sortSelectedColOpts$);

		const selectedOptions = sortColumnOptions
			.filter((_, key) => ids.includes(key))
			.map<AppColumnItemEntity>((option, key) =>
				AppColumnItemEntity.build({
					id: key,
					label: option.label,
					type: option.type,
					isList: option.isList,
					sort: 'ASC',
				})
			);

		const mergedSelected = sortSelectedColumns.merge(selectedOptions);

		this.sortSelectedColOpts$.next(mergedSelected);

		const availableColumns = await firstValueFrom(this.availableColumns$);

		const updatedOptions = availableColumns
			.filter((_, key) => !mergedSelected.keySeq().toArray().includes(key))
			.map<MultiSelectMenuOptionEntity>((item, key) =>
				MultiSelectMenuOptionEntity.build({
					id: key,
					label: item.label,
					type: item.type,
					isList: item.isList,
				})
			);

		this.sortColumnOptions$.next(updatedOptions);
	}

	public columnOrderModified($event: Array<AppColumnItemEntity>): void {
		const newSelectedMap = Map<number, AppColumnItemEntity>(
			$event.map((item) => [item.id, item])
		);
		this.selectedColOpts$.next(newSelectedMap);
	}

	public sortColumnOrderModified($event: Array<AppColumnItemEntity>): void {
		const newSelectedMap = Map<number, AppColumnItemEntity>(
			$event.map((item) => [item.id, item])
		);
		this.sortSelectedColOpts$.next(newSelectedMap);
	}

	public sortOrderModified($event: Array<AppColumnItemEntity>): void {
		const newSelectedMap = Map<number, AppColumnItemEntity>(
			$event.map((item) => [item.id, item])
		);
		this.sortSelectedColOpts$.next(newSelectedMap);
	}

	public async removeAllSelectedColumns(): Promise<void> {
		const availableColumns = await firstValueFrom(this.availableColumns$);

		const updatedOptions = availableColumns.map<MultiSelectMenuOptionEntity>(
			(item, key) =>
				MultiSelectMenuOptionEntity.build({
					id: key,
					label: item.label,
					type: item.type,
					isList: item.isList,
				})
		);

		this.selectedColOpts$.next(Map());
		this.columnOptions$.next(updatedOptions);
	}

	public async removeAllSortSelectedColumns(): Promise<void> {
		const availableColumns = await firstValueFrom(this.availableColumns$);

		const updatedOptions = availableColumns.map<MultiSelectMenuOptionEntity>(
			(item, key) =>
				MultiSelectMenuOptionEntity.build({
					id: key,
					label: item.label,
					type: item.type,
					isList: item.isList,
				})
		);

		this.sortSelectedColOpts$.next(Map());
		this.sortColumnOptions$.next(updatedOptions);
	}

	public async columnRemoved(item: AppColumnItemEntity): Promise<void> {
		const columnOptions = await firstValueFrom(this.columnOptions$);
		const selectedColumns = await firstValueFrom(this.selectedColOpts$);

		const updatedSelectedColumns = selectedColumns.filter(
			(col) => col.label !== item.label
		);

		this.selectedColOpts$.next(updatedSelectedColumns);

		const availableColumns = await firstValueFrom(this.availableColumns$);

		const option = availableColumns
			.filter((col) => col.label === item.label)
			.map<MultiSelectMenuOptionEntity>((item, key) =>
				MultiSelectMenuOptionEntity.build({
					id: key,
					label: item.label,
					type: item.type,
					isList: item.isList,
				})
			);

		const mergedOptions = columnOptions.merge(option);
		this.columnOptions$.next(mergedOptions);
	}

	public async sortColumnRemoved(item: AppColumnItemEntity): Promise<void> {
		const sortColumnOptions = await firstValueFrom(this.sortColumnOptions$);
		const sortSelectedColumns = await firstValueFrom(this.sortSelectedColOpts$);

		const updatedSelectedColumns = sortSelectedColumns.filter(
			(col) => col.label !== item.label
		);

		this.sortSelectedColOpts$.next(updatedSelectedColumns);

		const availableColumns = await firstValueFrom(this.availableColumns$);

		const option = availableColumns
			.filter((col) => col.label === item.label)
			.map<MultiSelectMenuOptionEntity>((item, key) =>
				MultiSelectMenuOptionEntity.build({
					id: key,
					label: item.label,
					type: item.type,
					isList: item.isList,
				})
			);

		const mergedOptions = sortColumnOptions.merge(option);
		this.sortColumnOptions$.next(mergedOptions);
	}

	public async simulationExec(): Promise<void> {
		const selectedDataSource = await firstValueFrom(this.selectedDatasource$);
		const selectedSimElements = await firstValueFrom(this.selectedSimElements$);

		if (selectedSimElements.size === 0) {
			return;
		}

		const simulationType = await firstValueFrom(this.simulationType$);

		const availableColumns = await firstValueFrom(this.availableColumns$);
		const selectedColumns = await firstValueFrom(this.selectedColOpts$);
		const sortSelectedColumns = await firstValueFrom(this.sortSelectedColOpts$);

		const displayColumns: Array<string> = [];
		const sortColumns: Array<SimulationSortColumnType> = [];

		let resultColumns =
			selectedColumns.size === 0
				? availableColumns
				: selectedColumns.map((_, key) => {
						const elem = availableColumns.get(
							key
						) as SimulationColumnsItemEntity;
						displayColumns.push(elem.id);
						return elem;
				  });

		sortSelectedColumns.entrySeq().forEach(([key, item], index) => {
			const elem = resultColumns.get(key);
			if (elem) {
				const direction = item.sort === 'DESC' ? 'desc' : 'asc';
				resultColumns = resultColumns.set(
					key,
					elem.setSortOrder(direction, index)
				);
				sortColumns.push({
					uuid: elem.id,
					order: direction,
				});
			}
		});

		this.simulationResultColumns$.next(
			Map<string, SimulationColumnsItemEntity>(
				resultColumns.entrySeq().map(([_, item]) => [item.alias, item])
			)
		);

		if (simulationType === 'PROFILE') {
			const selectedProfile = selectedSimElements.first();

			if (!selectedProfile) {
				return;
			}

			return this.store.dispatch(
				getSubscriptionSimulationData({
					dataSource: selectedDataSource,
					profile: selectedProfile.id,
					displayColumns,
					sortColumns,
				})
			);
		}

		const selectedColumnModel = selectedSimElements.find(
			(s) => s.type === 'COLUMN_MODEL'
		);

		const selectedFilterModels = selectedColumnModel
			? selectedSimElements.subtract([selectedColumnModel])
			: selectedSimElements;

		if (!selectedColumnModel) {
			return;
		}

		this.store.dispatch(
			getSubscriptionSimulationData({
				dataSource: selectedDataSource,
				profile: ProfileItemEntity.build({
					columnModelId: selectedColumnModel.id,
					filterModelIds: selectedFilterModels.map((f) => f.id),
				}),
				displayColumns,
				sortColumns,
			})
		);
	}

	private resetSimulation(): void {
		this.store.dispatch(resetSimulationData());
		if (this.columnOptions$.value.size !== 0) {
			this.columnOptions$.next(Map());
		}

		if (this.selectedColOpts$.value.size !== 0) {
			this.selectedColOpts$.next(Map());
		}

		if (this.sortColumnOptions$.value.size !== 0) {
			this.sortColumnOptions$.next(Map());
		}

		if (this.sortSelectedColOpts$.value.size !== 0) {
			this.sortSelectedColOpts$.next(Map());
		}
	}

	private async navigateToSubscription(): Promise<void> {
		const subscriptionId = await firstValueFrom(this.currentSubscription$);

		this.store.dispatch(
			navigateToSubscriptionDetails({
				subscriptionId,
			})
		);
	}

	private async triggerInit(): Promise<void> {
		const subscriptionId = await firstValueFrom(this.currentSubscription$);

		this.store.dispatch(
			getCurrentSubscriptionSimulationConfig({
				subscriptionId,
			})
		);
	}

	private handleSubscription(): void {
		this.availableColumns$
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => {
					setTimeout(
						() =>
							this.simulationViewProvider.adjustHeight(
								this.elemRef,
								this.renderer
							),
						750
					);
				}),
				map((items) =>
					items.map<MultiSelectMenuOptionEntity>((item, key) =>
						MultiSelectMenuOptionEntity.build({
							id: key,
							label: item.alias,
							type: item.type,
							isList: item.isList,
						})
					)
				),
				tap((options) => {
					this.columnOptions$.next(options);
					this.sortColumnOptions$.next(options);
				})
			)
			.subscribe();

		this.simulatorSelector
			.getSubscriptionStatus$()
			.pipe(
				takeUntil(this.onDestroy$),
				tap((status) => {
					this.dataSourceType = [
						{
							id: 1,
							label: $localize`:i18n=@@subscription.simulator.dataSource.datablock:`,
							isSelected: status === SubscriptionStatus.Unpublish,
							cb: (): void => this.selectedDatasource$.next('DATABLOCK'),
						},
						{
							id: 2,
							label: $localize`:i18n=@@subscription.simulator.dataSource.publishedData:`,
							isDisabled: status === SubscriptionStatus.Unpublish,
							isSelected: status !== SubscriptionStatus.Unpublish,
							cb: (): void => this.selectedDatasource$.next('PUBLISH_DATA'),
						},
					];

					if (status === SubscriptionStatus.Unpublish) {
						this.selectedDatasource$.next('DATABLOCK');
					} else {
						this.selectedDatasource$.next('PUBLISH_DATA');
					}
				})
			)
			.subscribe();

		this.selectedDatasource$
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(this.simulatorSelector.getSimulationResult$()),
				tap(([, result]) => {
					if (!result) {
						return;
					}
					this.store.dispatch(resetSimulationData());
				})
			)
			.subscribe();

		combineAll({
			selectedProfile:
				this.simulatorSelector.getPreSelectedProfileForSimulation$(),
			selectedColumnModel:
				this.simulatorSelector.getPreSelectedColumnModelForSimulation$(),
		})
			.pipe(
				takeUntil(this.onDestroy$),
				tap(({ selectedProfile, selectedColumnModel }) => {
					const nothingSelected =
						selectedProfile === '' && selectedColumnModel === '';
					this.simulateTypeOptions = [
						{
							id: 1,
							label: $localize`:i18n=@@subscription.simulator.profiles:`,
							isSelected: nothingSelected || selectedProfile !== '',
							icon: DcIcons.Profile,
							cb: () => this.getAvailableSimulationData('PROFILE'),
						},
						{
							id: 2,
							label: $localize`:i18n=@@subscription.simulator.models:`,
							isSelected: selectedColumnModel !== '',
							icon: DcIcons.Column,
							cb: () => this.getAvailableSimulationData('MODELS'),
						},
					];

					if (selectedProfile === '' && selectedColumnModel === '') {
						return;
					}

					if (selectedProfile) {
						this.simulationType$.next('PROFILE');
					} else {
						this.simulationType$.next('MODELS');
					}
				})
			)
			.subscribe();

		combineAll({
			profiles: this.simulatorSelector.getProfilesForSimulation$(),
			columnModels: this.simulatorSelector.getColumnModelsForSimulation$(),
		})
			.pipe(
				takeUntil(this.onDestroy$),
				filter(
					({ profiles, columnModels }) =>
						profiles.size !== 0 && columnModels.size !== 0
				),
				withLatestFrom(
					this.simulatorSelector.getPreSelectedProfileForSimulation$(),
					this.simulatorSelector.getPreSelectedColumnModelForSimulation$()
				),
				tap(
					([
						{ profiles, columnModels },
						selectedProfileId,
						selectedColumnModelId,
					]) => {
						if (selectedProfileId === '' && selectedColumnModelId === '') {
							return;
						}

						let selectedElement: SelectElement;
						let columnModel: ColumnModelItemEntity;
						if (selectedProfileId) {
							const selectedProfile = profiles.get(selectedProfileId);

							if (!selectedProfile) {
								return;
							}

							columnModel = columnModels.get(
								selectedProfile.columnModelId
							) as ColumnModelItemEntity;

							selectedElement = {
								id: selectedProfile.id,
								label: selectedProfile.label,
								type: 'PROFILE',
							};
						} else {
							const selectedColumnModel = columnModels.get(
								selectedColumnModelId
							);

							if (!selectedColumnModel) {
								return;
							}

							columnModel = selectedColumnModel;

							selectedElement = {
								id: selectedColumnModel.id,
								label: selectedColumnModel.label,
								type: 'COLUMN_MODEL',
							};
						}

						const simulationColumns =
							this.generateSimulationColumns(columnModel);

						this.simulationColumns$.next(simulationColumns);
						this.selectedSimElements$.next(Set([selectedElement]));
					}
				)
			)
			.subscribe();

		this.simulatorSelector
			.getSubscriptionColumns$()
			.pipe(
				takeUntil(this.onDestroy$),
				tap((columns) => {
					const filtered = columns.filter(
						(v) => !v.isList && v.isSelectedInSource
					);
					const fields =
						this.columnsQueryBuilderProvider.convertToQueryBuilderField(
							filtered
						);
					this.availableFields$.next(fields);
				})
			)
			.subscribe();

		combineAll({
			metadata: this.simulatorSelector.getSimulationMetadata$(),
			projectLabel: this.toObservable<string>('projectLabel'),
		})
			.pipe(
				takeUntil(this.onDestroy$),
				tap(({ metadata, projectLabel }) => {
					this.paths = [
						{
							label: $localize`:i18n=@@general.projects:`,
							isActive: true,
							callback: (): void => this.store.dispatch(goToProjects()),
						},
						{
							label: projectLabel,
							isActive: true,
							callback: (): void => this.store.dispatch(goToHdHome()),
						},
						{
							label: $localize`:i18n=@@subscription.breadcrumb.list:`,
							isActive: true,
							tooltip: $localize`:i18n=@@subscription.breadcrumb.list.tooltip:`,
							callback: (): void =>
								this.store.dispatch(navigateToSubscriptionList()),
						},
						{
							label: metadata.title,
							isActive: true,
							callback: this.navigateToSubscription.bind(this),
						},
						{
							label: $localize`:i18n=@@subscription.breadcrumb.simulator:`,
							isActive: false,
							callback: (): void => {},
						},
					];
				})
			)
			.subscribe();
	}

	private generateSimulationColumns(
		columnModel: ColumnModelItemEntity
	): Map<string, SimulationColumnsItemEntity> {
		const tuples = columnModel.columns.reduce((acc, curr) => {
			const entity = SimulationColumnsItemEntity.build({
				id: curr.id,
				label: curr.label,
				alias: curr.alias,
				description: curr.description,
				type: curr.columnType,
				isList: curr.isList,
				isHashed: curr.isHashed,
				dxDataType: ColumnTypeToDxColumnType[curr.columnType],
			});
			acc.push([`${entity.id}`, entity]);
			return acc;
		}, new Array<[string, SimulationColumnsItemEntity]>());
		return Map(tuples);
	}
}
